import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  Award, 
  Users, 
  Globe2,
  CheckCircle2,
  Cog,
  Sparkles,
  Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Section, SectionTitle, FadeIn, StaggerContainer, StaggerItem } from '../components/Section';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';

// Company founded in 2013
const FOUNDING_YEAR = 2013;
const currentYear = new Date().getFullYear();
const yearsExperience = currentYear - FOUNDING_YEAR;

const valueIcons = [Cog, Sparkles, Users, Shield, Award, Globe2];

export default function About() {
  const { t, language } = useLanguage();

  const values = {
    en: [
      { title: 'Engineering Rigor', description: 'Every project is approached with technical precision and adherence to international engineering standards.' },
      { title: 'Aesthetic Ambition', description: 'We believe fountains should inspire. Design excellence is at the heart of every concept we develop.' },
      { title: 'Technical Assistance', description: 'We partner with architects, engineers, and consultants to ensure seamless project execution.' },
      { title: 'Reliable Execution', description: 'From concept to commissioning, we deliver on our commitments with transparency and professionalism.' },
      { title: 'Premium Ecosystem', description: 'Access to world-class fountain equipment and technologies from leading European manufacturers.' },
      { title: 'International Expertise', description: 'Deep understanding of executing projects across diverse international markets and contexts.' },
    ],
    fr: [
      { title: 'Rigueur Ingénierie', description: "Chaque projet est abordé avec précision technique et respect des normes d'ingénierie internationales." },
      { title: 'Ambition Esthétique', description: "Nous croyons que les fontaines doivent inspirer. L'excellence du design est au cœur de chaque concept." },
      { title: 'Assistance Technique', description: 'Nous collaborons avec architectes, ingénieurs et consultants pour une exécution de projet sans faille.' },
      { title: 'Exécution Fiable', description: 'Du concept à la mise en service, nous tenons nos engagements avec transparence et professionnalisme.' },
      { title: 'Écosystème Premium', description: 'Accès aux équipements et technologies de fontaines de classe mondiale des fabricants européens leaders.' },
      { title: 'Expertise Internationale', description: "Compréhension approfondie de l'exécution de projets sur divers marchés internationaux." },
    ],
    es: [
      { title: 'Rigor de Ingeniería', description: 'Cada proyecto se aborda con precisión técnica y cumplimiento de estándares de ingeniería internacionales.' },
      { title: 'Ambición Estética', description: 'Creemos que las fuentes deben inspirar. La excelencia en diseño está en el corazón de cada concepto.' },
      { title: 'Asistencia Técnica', description: 'Colaboramos con arquitectos, ingenieros y consultores para una ejecución de proyecto impecable.' },
      { title: 'Ejecución Confiable', description: 'Del concepto a la puesta en marcha, cumplimos nuestros compromisos con transparencia y profesionalismo.' },
      { title: 'Ecosistema Premium', description: 'Acceso a equipos y tecnologías de fuentes de clase mundial de fabricantes europeos líderes.' },
      { title: 'Experiencia Internacional', description: 'Comprensión profunda de la ejecución de proyectos en diversos mercados internacionales.' },
    ],
  };

  const currentValues = values[language] || values.en;

  return (
    <main data-testid="about-page">
      <SEO page="about" />
      {/* Hero Section */}
      <section className="relative py-24 lg:py-32 hero-gradient overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-10 right-20 w-80 h-80 rounded-full bg-[#29ABE2]/5 blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className="text-sm font-medium uppercase tracking-widest text-[#29ABE2] mb-4 block">
              {t('about.eyebrow')}
            </span>
            <h1 className="text-5xl lg:text-6xl font-bold text-[#1A1A1A] tracking-tight uppercase mb-6">
              {t('about.title1')}<br />
              {t('about.title2')}
            </h1>
            <p className="text-xl text-[#6C757D] leading-relaxed">
              {t('about.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Story */}
      <Section background="white">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <FadeIn>
            <img
              src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwyfHxjb25zdHJ1Y3Rpb24lMjBzaXRlfGVufDB8fHx8MTc3Mzg1MzU5OXww&ixlib=rb-4.1.0&q=85"
              alt="Engineering excellence"
              className="w-full aspect-[4/3] object-cover rounded-sm"
            />
          </FadeIn>
          <FadeIn delay={0.2}>
            <SectionTitle
              eyebrow={t('about.storyEyebrow')}
              title={t('about.storyTitle')}
            />
            <div className="space-y-4 text-[#6C757D] leading-relaxed">
              <p>{t('about.storyP1')}</p>
              <p>{t('about.storyP2')}</p>
              <p>{t('about.storyP3')}</p>
            </div>
            <div className="mt-8">
              <Link to="/contact">
                <Button
                  data-testid="about-cta-btn"
                  className="bg-[#1A1A1A] text-white hover:bg-[#29ABE2] rounded-none px-8 py-4 text-sm font-bold uppercase tracking-wider"
                >
                  {t('about.startConversation')}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* Stats Section */}
      <Section background="surface" paddingY="py-16 lg:py-20">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          <FadeIn delay={0}>
            <div className="text-center">
              <p className="stat-number text-5xl lg:text-6xl mb-2">{yearsExperience}+</p>
              <p className="text-sm text-[#6C757D] uppercase tracking-wider">{t('stats.years')}</p>
            </div>
          </FadeIn>
          <FadeIn delay={0.1}>
            <div className="text-center">
              <p className="stat-number text-5xl lg:text-6xl mb-2">200+</p>
              <p className="text-sm text-[#6C757D] uppercase tracking-wider">{t('stats.projects')}</p>
            </div>
          </FadeIn>
          <FadeIn delay={0.2}>
            <div className="text-center">
              <p className="stat-number text-5xl lg:text-6xl mb-2">10+</p>
              <p className="text-sm text-[#6C757D] uppercase tracking-wider">{t('stats.countries')}</p>
            </div>
          </FadeIn>
          <FadeIn delay={0.3}>
            <div className="text-center">
              <p className="stat-number text-5xl lg:text-6xl mb-2">100%</p>
              <p className="text-sm text-[#6C757D] uppercase tracking-wider">{t('stats.satisfaction')}</p>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* Our Values */}
      <Section background="white">
        <SectionTitle
          eyebrow={t('about.valuesEyebrow')}
          title={t('about.valuesTitle')}
          description={t('about.valuesDescription')}
          align="center"
        />
        <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {currentValues.map((value, index) => {
            const Icon = valueIcons[index] || Cog;
            return (
              <StaggerItem key={index}>
                <div className="p-8 border border-gray-100 bg-white hover:border-[#29ABE2]/30 transition-colors duration-300">
                  <div className="w-12 h-12 bg-[#29ABE2]/10 rounded-full flex items-center justify-center mb-6">
                    <Icon className="w-6 h-6 text-[#29ABE2]" />
                  </div>
                  <h3 className="text-xl font-bold text-[#1A1A1A] mb-3 uppercase tracking-wide">
                    {value.title}
                  </h3>
                  <p className="text-sm text-[#6C757D] leading-relaxed">
                    {value.description}
                  </p>
                </div>
              </StaggerItem>
            );
          })}
        </StaggerContainer>
      </Section>

      {/* International Credibility */}
      <Section background="dark">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <FadeIn>
            <SectionTitle
              eyebrow={t('about.internationalEyebrow')}
              title={t('about.internationalTitle')}
              description={t('about.internationalDescription')}
              dark
            />
            <div className="space-y-4 mt-8">
              {(t('about.internationalPoints') || []).map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#29ABE2] flex-shrink-0" />
                  <span className="text-gray-300">{item}</span>
                </div>
              ))}
            </div>
          </FadeIn>
          <FadeIn delay={0.2}>
            <img
              src="https://images.unsplash.com/photo-1694521787162-5373b598945c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwzfHxjb25zdHJ1Y3Rpb24lMjBzaXRlfGVufDB8fHx8MTc3Mzg1MzU5OXww&ixlib=rb-4.1.0&q=85"
              alt="International operations"
              className="w-full aspect-[4/3] object-cover rounded-sm"
            />
          </FadeIn>
        </div>
      </Section>

      {/* CTA Section */}
      <Section background="white" paddingY="py-20 lg:py-28">
        <div className="text-center max-w-3xl mx-auto">
          <FadeIn>
            <h2 className="text-4xl lg:text-5xl font-bold text-[#1A1A1A] tracking-tight uppercase mb-6">
              {t('about.readyTitle')}
            </h2>
            <p className="text-lg text-[#6C757D] mb-8">
              {t('about.readyDescription')}
            </p>
            <Link to="/contact">
              <Button
                data-testid="about-final-cta-btn"
                className="bg-[#29ABE2] text-white hover:bg-[#0077B6] rounded-none px-8 py-6 text-sm font-bold uppercase tracking-wider"
              >
                {t('about.contactUs')}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </FadeIn>
        </div>
      </Section>
    </main>
  );
}
